import { App } from 'antd'
import { Centrifuge, UnauthorizedError } from 'centrifuge'
import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'

import { useChatRealtime, isChatRealtimeEvent } from '@/Containers/CommunicationSection/ChatContainer/Hooks/useChatRealtime'
import { useChatPresence } from '@/Containers/CommunicationSection/ChatContainer/Hooks/useChatPresence'
import {
    handleNotificationRealtime,
    notificationToast,
} from '@/Containers/CommunicationSection/NotificationContainer/Hooks/notificationRealtime'
import { useNotification } from '@/Core/Hooks/useNotification'
import { userManager } from '@/Core/Managers'
import { COUNTERS_EVENT, counterManager } from '@/Ship/Stores/CounterManager'

// Доступ через індекс — tsconfig проєкту вмикає noPropertyAccessFromIndexSignature
const VITE_BASE_DOMAIN = import.meta.env['VITE_BASE_DOMAIN']

/**
 * Лічильники меню перечитуються після кожного (пере)підписання каналу — за
 * час розриву подія `counters` могла загубитися. Перше підписання приходить
 * за мить після старту оболонки, яка лічильники вже прочитала: свіжіші за
 * цей вік не перечитуємо, бо запит на стенді коштує секунду воркера.
 */
const COUNTERS_MAX_AGE_MS = 10_000

/**
 * Єдине з'єднання з Centrifugo на застосунок: канал користувача
 * (`user.<uuid>`) плюс presence-канали чату.
 *
 * Перенесено з autostrada разом із чатом. Дві їхні частини сюди НЕ їдуть:
 *   • `useNotificationRealtime` — тягне їхній `NotificationManager`, тобто
 *     заміну всього нашого NotificationContainer (дзвіночок уже працює);
 *   • клікабельний тост на `entityCode === 'Assignment'` — їхній домен.
 * Порядок гілок публікації (`SPEC-014` §3.2, Р12):
 *   1. `counters` — лічильники бейджів меню; без тосту, інакше кожна зміна
 *      справи давала б «Нове сповіщення»;
 *   2. події чату;
 *   3. сповіщення дзвіночка — тост за кодом із каталогу (`NotificationKinds`).
 */
export const CentrifugoConnector = observer(() => {
    const { showError } = useNotification()
    const { notification } = App.useApp()
    const { onChatEvent } = useChatRealtime()
    const { attachPresence } = useChatPresence()

    // Keep the latest callbacks in a ref so the subscription effect does NOT
    // depend on their identity. `useNotification` returns fresh
    // showError on every render; if they were in the deps, the effect would
    // tear down and re-create the Centrifugo connection on each render and
    // realtime publications (e.g. the unread counter) would be lost until a
    // full page reload.
    const handlersRef = useRef({ showError, onChatEvent, notification })
    handlersRef.current = { showError, onChatEvent, notification }

    const connectionToken = userManager.centrifugoConnectionToken
    const subscriptionToken = userManager.centrifugoSubscriptionToken
    const userUuid = userManager.user?.['uuid']

    useEffect(() => {
        if (!userManager.isAuthorized) return

        if (!connectionToken || !subscriptionToken || !userUuid) {
            console.warn('[CentrifugoConnector] Missing required tokens or user UUID')
            return
        }

        const centrifugoUrl = `wss://centrifugo.${VITE_BASE_DOMAIN}/connection/websocket`

        const userChannel = `user.${userUuid}`

        // getToken: self-healing on token expiry. Centrifugo tokens live 1h
        // from login/refresh; with a STATIC token centrifuge-js treats error
        // 109 ("token expired") as unrecoverable and never reconnects — any
        // tab older than an hour silently lost realtime. With getToken the
        // SDK asks us for a fresh token instead: we refresh the session
        // (refresh_token endpoint issues new centrifugo tokens, see
        // AppConfig ?centrifugo=true) and hand the new token back.
        const refreshCentrifugoToken = async (pick: () => string | null): Promise<string> => {
            const existing = pick()
            const refreshed = await userManager.refreshAccessToken()
            const token = pick()
            if (!refreshed || !token || token === existing) {
                // Session could not be refreshed (refresh token expired after
                // 1h idle → UserManager already logged the user out).
                // UnauthorizedError is the centrifuge-js contract for "stop
                // retrying permanently" — a plain Error would mean "transient,
                // retry with backoff" and spam an error toast on every attempt.
                throw new UnauthorizedError('Session expired, relogin required')
            }
            return token
        }

        const centrifuge = new Centrifuge(centrifugoUrl, {
            token: connectionToken,
            getToken: () => refreshCentrifugoToken(() => userManager.centrifugoConnectionToken),
        })

        const sub = centrifuge.newSubscription(userChannel, {
            token: subscriptionToken,
            getToken: () => refreshCentrifugoToken(() => userManager.centrifugoSubscriptionToken),
        })

        // Лічильники меню: на першому підписанні — якщо оболонка ще не прочитала,
        // на кожному наступному (після розриву) — завжди
        sub.on('subscribed', () => {
            void counterManager.loadIfStale(COUNTERS_MAX_AGE_MS)
        })

        sub.on('publication', (ctx) => {
            const data = ctx.data || {}

            // Лічильники бейджів меню — стан, а не новина: без тосту (Р12)
            if (data.event === COUNTERS_EVENT) {
                counterManager.apply(data.counters)
                return
            }

            // Chat events ({ type: 'message:new', data }) have their own
            // handler and must not fall through to the notification toast
            // below — otherwise every chat message would spam "Нове сповіщення".
            if (isChatRealtimeEvent(data)) {
                handlersRef.current.onChatEvent(data)
                return
            }

            // Дзвіночок і відкрита шухляда сповіщень дізнаються про подію одразу
            handleNotificationRealtime(data)

            // Тост лише на нове сповіщення (видалення — не новина): підпис і
            // тип — з каталогу за кодом
            const toast = notificationToast(data)
            if (!toast) return

            handlersRef.current.notification[toast.type]({
                key: `notification-${Date.now()}`,
                title: toast.title,
                description: toast.description,
                placement: 'topRight',
                duration: 5,
                showProgress: true,
                pauseOnHover: true,
            })
        })

        sub.on('error', (err) => {
            // SubscriptionErrorContext = { channel, type, error } — текст лежить
            // у вкладеному error, а не на самому контексті
            handlersRef.current.showError('Помилка Centrifugo', err.error.message)
        })

        sub.subscribe()
        centrifuge.connect()

        // Presence-канал чату — друга підписка на цьому ж з'єднанні;
        // attachPresence стабільний (useCallback []), ефект не перезапускає
        const detachPresence = attachPresence(centrifuge)

        return () => {
            detachPresence()
            sub.unsubscribe()
            centrifuge.disconnect()
        }
        // Only re-create the connection when the identity/credentials change
        // (login, token refresh) — not on every render.
    }, [connectionToken, subscriptionToken, userUuid, attachPresence])

    return null
})

CentrifugoConnector.displayName = 'CentrifugoConnector'
